import React, { useMemo } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import rehypeRaw from "rehype-raw";

interface MarkdownRendererProps {
  content: string;
  className?: string;
  references?: any[];
  onReferenceClick?: (reference: any, isDirectLink?: boolean) => void;
}

// Process custom highlighting syntax ==highlighted==text==/highlighted==
const processHighlightSyntax = (text: string): string => {
  let processedText = text;
  let highlightIndex = 0;
  
  // Replace custom highlighting syntax with HTML mark tags
  processedText = processedText.replace(/==highlighted==(.*?)==\/highlighted==/gs, (match, content) => {
    return `<mark id="highlight-${highlightIndex++}" class="search-highlight">${content}</mark>`;
  });
  
  return processedText;
};

// Pre-process content to handle Steiner's custom tags
// Only escape tags that are NOT standard HTML or our highlight marks
const preserveTags = new Set([
  'mark', 'p', 'div', 'span', 'a', 'b', 'i', 'em', 'strong',
  'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'ul', 'ol', 'li',
  'table', 'tr', 'td', 'th', 'thead', 'tbody', 'br', 'hr',
  'img', 'code', 'pre', 'blockquote', 'sup', 'sub'
]);

const preprocessContent = (text: string): string => {
  // Replace opening tags
  text = text.replace(/<([^>\s/]+)([^>]*)>/g, (match, tagName, attributes) => {
    if (preserveTags.has(tagName.toLowerCase())) return match;
    return `&lt;${tagName}${attributes}&gt;`;
  });

  // Replace closing tags
  text = text.replace(/<\/([^>\s]+)>/g, (match, tagName) => {
    if (preserveTags.has(tagName.toLowerCase())) return match;
    return `&lt;/${tagName}&gt;`;
  });

  return text;
};

// Stable remark/rehype plugin arrays (avoid re-creating on every render)
const remarkPlugins = [remarkGfm];
const rehypePlugins = [rehypeRaw];

export const MarkdownRenderer: React.FC<MarkdownRendererProps> = React.memo(({
  content,
  className = "",
  references = [],
  onReferenceClick
}) => {
  // Memoize content processing (expensive regex operations)
  const { processedContent, referenceMap } = useMemo(() => {
    const highlightProcessed = processHighlightSyntax(content);
    const preprocessedContent = preprocessContent(highlightProcessed);
    return processReferences(preprocessedContent, references);
  }, [content, references]);

  // Memoize the components object to avoid re-creating on every render
  const markdownComponents = useMemo(() => ({
    h1: ({ children }: any) => <h1 className="markdown-h1">{children}</h1>,
    h2: ({ children }: any) => <h2 className="markdown-h2">{children}</h2>,
    h3: ({ children }: any) => <h3 className="markdown-h3">{children}</h3>,
    h4: ({ children }: any) => <h4 className="markdown-h4">{children}</h4>,
    h5: ({ children }: any) => <h5 className="markdown-h5">{children}</h5>,
    h6: ({ children }: any) => <h6 className="markdown-h6">{children}</h6>,
    p: ({ children }: any) => <p className="markdown-p">{children}</p>,
    ul: ({ children }: any) => <ul className="markdown-ul">{children}</ul>,
    ol: ({ children }: any) => <ol className="markdown-ol">{children}</ol>,
    li: ({ children }: any) => <li className="markdown-li">{children}</li>,
    blockquote: ({ children }: any) => (
      <blockquote className="markdown-blockquote">{children}</blockquote>
    ),
    code: ({ children, className }: any) => {
      const isInline = !className;
      return isInline ? (
        <code className="markdown-code-inline">{children}</code>
      ) : (
        <code className={`markdown-code-block ${className}`}>{children}</code>
      );
    },
    pre: ({ children }: any) => <pre className="markdown-pre">{children}</pre>,
    strong: ({ children }: any) => <strong className="markdown-strong">{children}</strong>,
    em: ({ children }: any) => <em className="markdown-em">{children}</em>,
    a: ({ href, children }: any) => {
      const isRefLink = href && href.startsWith('#ref-');
      const isNumberLink = /^\d+$/.test(String(children)) && references.length > 0;

      if (isRefLink || isNumberLink) {
        let refNumber: number;
        let reference: any;

        if (isRefLink) {
          refNumber = parseInt(href.replace('#ref-', ''));
          reference = referenceMap.get(refNumber);
        } else if (isNumberLink) {
          refNumber = parseInt(String(children));
          reference = referenceMap.get(refNumber);
          if (!reference && refNumber >= 1 && refNumber <= references.length) {
            reference = references[refNumber - 1];
          }
        }

        if (reference && onReferenceClick) {
          const getDocumentTooltip = (ref: any) => {
            const docName = ref.document_name || 'Dokument';
            if (ref.title && ref.title.trim()) {
              return `${docName}: ${ref.title}`;
            }
            if (ref.ga_number && ref.title) {
              return `${ref.ga_number}: ${ref.title}`;
            }
            const title = docName.replace(/\.[^/.]+$/, "").replace(/_/g, " ");
            return title.replace(/\b\w/g, (l: string) => l.toUpperCase());
          };

          return (
            <button
              type="button"
              className="markdown-link reference-link"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                onReferenceClick(reference, true);
              }}
              title={getDocumentTooltip(reference)}
            >
              {children}
            </button>
          );
        } else if (isRefLink) {
          return (
            <span className="reference-link">
              {children}
            </span>
          );
        }
      }

      return (
        <a
          href={href}
          className="markdown-link"
          target="_blank"
          rel="noopener noreferrer"
        >
          {children}
        </a>
      );
    },
    table: ({ children }: any) => (
      <div className="markdown-table-wrapper">
        <table className="markdown-table">{children}</table>
      </div>
    ),
    thead: ({ children }: any) => <thead className="markdown-thead">{children}</thead>,
    tbody: ({ children }: any) => <tbody className="markdown-tbody">{children}</tbody>,
    tr: ({ children }: any) => <tr className="markdown-tr">{children}</tr>,
    th: ({ children }: any) => <th className="markdown-th">{children}</th>,
    td: ({ children }: any) => <td className="markdown-td">{children}</td>,
    img: ({ src, alt, title }: any) => (
      <img
        src={src}
        alt={alt || 'Bild'}
        title={title}
        className="markdown-image"
      />
    ),
  }), [references, referenceMap, onReferenceClick]);

  return (
    <div className={`markdown-content ${className}`}>
      <ReactMarkdown
        remarkPlugins={remarkPlugins}
        rehypePlugins={rehypePlugins}
        components={markdownComponents}
      >
        {processedContent}
      </ReactMarkdown>
    </div>
  );
});

// Process reference patterns and convert them to markdown links
// Supports backend formats: [Quelle X], [Quelle X, S. Y], [Quelle X, Y], [Quelle X, Y, Z], [Quelle X, Quelle Y]
const processReferences = (text: string, references: any[]): { processedContent: string; referenceMap: Map<number, any> } => {
  const referenceMap = new Map<number, any>();

  // Unified pattern: matches all [Quelle ...] variants
  const anyQuellePattern = /\[Quelle\s[^\]]*\d[^\]]*\]/g;

  // Legacy patterns (fallback)
  const referencePattern = /##(\d+)\$\$/g;
  const bracketPattern = /\[(\d+)\]/g;
  const idPattern = /\[ID:(\d+)\]/g;

  // Check if any [Quelle ...] pattern is present
  const hasQuellePattern = anyQuellePattern.test(text);
  anyQuellePattern.lastIndex = 0;

  if (hasQuellePattern) {
    // Extract ALL numbers from ALL [Quelle ...] patterns
    const quelleNumbers = new Set<number>();
    const allQuelleMatches = Array.from(text.matchAll(anyQuellePattern));
    allQuelleMatches.forEach(match => {
      const nums = match[0].match(/\d+/g);
      if (nums) nums.forEach(n => quelleNumbers.add(parseInt(n)));
    });

    // Build an index-based lookup: ref.index (0-based) + 1 = Quelle number
    const refByIndex = new Map<number, any>();
    references.forEach((ref: any, arrayIdx: number) => {
      const refNum = (ref.index != null ? ref.index : arrayIdx) + 1;
      refByIndex.set(refNum, ref);
    });

    quelleNumbers.forEach(num => {
      if (refByIndex.has(num)) {
        referenceMap.set(num, refByIndex.get(num));
      } else if (num >= 1 && num - 1 < references.length) {
        referenceMap.set(num, references[num - 1]);
      }
    });

    // Replace all [Quelle ...] patterns in one pass
    let processedContent = text;
    anyQuellePattern.lastIndex = 0;
    processedContent = processedContent.replace(anyQuellePattern, (match) => {
      const nums = match.match(/\d+/g);
      if (!nums) return match;

      // Check for page reference: [Quelle X, S. Y]
      const pageMatch = match.match(/Quelle\s+(\d+),\s*S\.\s*(\d+)/);
      if (pageMatch && nums.length === 2) {
        const num = parseInt(pageMatch[1]);
        const page = pageMatch[2];
        if (referenceMap.has(num)) {
          return `[Quelle ${num}, S. ${page}](#ref-${num} "Quelle ${num}")`;
        }
        return '';
      }

      // Multi-number: [Quelle 3, 4] or [Quelle 1, 4, 10] or [Quelle 1, Quelle 2]
      return nums.map((n: string) => {
        const num = parseInt(n);
        if (referenceMap.has(num)) {
          return `[${num}](#ref-${num} "Quelle ${num}")`;
        }
        return '';
      }).filter(Boolean).join(', ');
    });

    return { processedContent, referenceMap };
  }

  // Legacy fallback: ##n$$, [n], [ID:n] patterns
  const allReferenceNumbers = new Set<number>();

  const hashMatches = Array.from(text.matchAll(referencePattern));
  hashMatches.forEach(match => allReferenceNumbers.add(parseInt(match[1])));

  const bracketMatches = Array.from(text.matchAll(bracketPattern));
  bracketMatches.forEach(match => {
    const num = parseInt(match[1]);
    if (num < 1900 && match[1].length < 4) {
      allReferenceNumbers.add(num);
    }
  });

  const idMatches = Array.from(text.matchAll(idPattern));
  idMatches.forEach(match => allReferenceNumbers.add(parseInt(match[1])));

  if (allReferenceNumbers.size === 0) {
    return { processedContent: text, referenceMap };
  }

  const sortedOriginalIds = Array.from(allReferenceNumbers).sort((a, b) => a - b);
  const idToDisplayNumber = new Map<number, number>();

  sortedOriginalIds.forEach((originalId, index) => {
    const displayNumber = index + 1;
    idToDisplayNumber.set(originalId, displayNumber);
    if (index < references.length) {
      referenceMap.set(displayNumber, references[index]);
    }
  });

  let processedContent = text;

  processedContent = processedContent.replace(referencePattern, (match, originalNumber) => {
    const originalId = parseInt(originalNumber);
    const displayNumber = idToDisplayNumber.get(originalId);
    if (displayNumber !== undefined) {
      return `[${displayNumber}](#ref-${displayNumber} "Quelle ${displayNumber}")`;
    }
    return match;
  });

  processedContent = processedContent.replace(bracketPattern, (match, number) => {
    const originalId = parseInt(number);
    if (originalId >= 1900 || number.length >= 4) {
      return match;
    }
    const displayNumber = idToDisplayNumber.get(originalId);
    if (displayNumber !== undefined && referenceMap.has(displayNumber)) {
      return `[${displayNumber}](#ref-${displayNumber} "Quelle ${displayNumber}")`;
    }
    return match;
  });

  processedContent = processedContent.replace(idPattern, (match, number) => {
    const originalId = parseInt(number);
    const displayNumber = idToDisplayNumber.get(originalId);
    if (displayNumber !== undefined && referenceMap.has(displayNumber)) {
      return `[${displayNumber}](#ref-${displayNumber} "Quelle ${displayNumber}")`;
    }
    return match;
  });

  return { processedContent, referenceMap };
};

export default MarkdownRenderer;